import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import AdminDashboard from './components/AdminDashboard';
import CustomerDashboard from './components/CustomerDashboard';
import DriverDashboard from './components/DriverDashboard';
import Login from './components/Login';
import Register from './components/Register';
import Verify from './components/Verify';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PaymentCallback from './components/PaymentCallback';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check localStorage on load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    const name = localStorage.getItem('userName');
    const id = localStorage.getItem('userId');

    if (token && role) {
      setIsAuthenticated(true);
      setUserRole(role);
      setUserName(name);
      setUserId(id);
    }
    setLoading(false);
  }, []);

  // ==================== SERVICE WORKER REGISTRATION ====================
  useEffect(() => {
    // Register service worker for push notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('✅ Service Worker registered successfully:', registration);
          
          // Check for existing push subscription
          registration.pushManager.getSubscription()
            .then((subscription) => {
              if (subscription) {
                console.log('📱 Existing push subscription found:', subscription.endpoint);
              } else {
                console.log('📱 No existing push subscription');
              }
            })
            .catch((err) => console.error('Error checking subscription:', err));
        })
        .catch((error) => {
          console.error('❌ Service Worker registration failed:', error);
        });

      // Handle service worker updates
      navigator.serviceWorker.ready
        .then((registration) => {
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            console.log('🔄 New service worker installing:', newWorker);
            
            newWorker.addEventListener('statechange', () => {
              console.log('📦 Service worker state:', newWorker.state);
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('🔄 New service worker installed, reloading...');
                window.location.reload();
              }
            });
          });
        })
        .catch((err) => console.error('Service worker ready error:', err));
    } else {
      console.warn('⚠️ Service workers not supported in this browser');
    }
  }, []);

  // ==================== EMERGENCY EVENT LISTENERS ====================




  // ==================== EMERGENCY EVENT LISTENERS ====================
  useEffect(() => {
    // Guard: some browsers/contexts have no serviceWorker API
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleEmergencyMessage = (event) => {
      if (event.data && event.data.type === 'EMERGENCY_TRIGGERED') {
        console.log('🚨 Emergency triggered from service worker:', event.data);

        if (event.data.driverId && event.data.driverId === userId) {
          alert('🚨 EMERGENCY LOCATE ACTIVE!\n\nYour phone is being located by admin. Please check the app for details.');
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleEmergencyMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleEmergencyMessage);
    };
  }, [userId]);





  // ==================== REQUEST NOTIFICATION PERMISSION ====================
  useEffect(() => {
    // Request notification permission when user logs in
    if (isAuthenticated && userRole === 'driver') {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
          .then((permission) => {
            if (permission === 'granted') {
              console.log('✅ Notification permission granted');
            } else {
              console.log('❌ Notification permission denied');
            }
          })
          .catch((err) => console.error('Notification permission error:', err));
      }
    }
  }, [isAuthenticated, userRole]);

  // ==================== REGISTER PUSH WITH BACKEND ====================




  const registerPushNotifications = async () => {
    if (!userId || userRole !== 'driver') return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    if (!('PushManager' in window)) return;

    try {
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;



      
      // Check if push manager is available
      if (!registration.pushManager) {
        console.log('Push manager not available');
        return;
      }

      // Get existing subscription or create new one
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Get VAPID public key from environment
        const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        
        if (!vapidPublicKey) {
          console.warn('VAPID public key not set. Push notifications will not work.');
          return;
        }

        // Convert VAPID key to Uint8Array
        const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('✅ New push subscription created');
      }

      // Get phone number from localStorage
      const phoneNumber = localStorage.getItem('driverPhone') || '';

      // Send subscription to backend
      const token = localStorage.getItem('token');
      const response = await fetch('/api/emergency/register-push', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          driverId: userId,
          pushSubscription: subscription,
          phoneNumber: phoneNumber
        })
      });

      if (response.ok) {
        console.log('✅ Push subscription registered with backend');
      } else {
        console.error('❌ Failed to register push with backend');
      }
    } catch (error) {
      console.error('❌ Push registration error:', error);
    }
  };

  // Helper function to convert base64 to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Register push when user is authenticated and is a driver
  useEffect(() => {
    if (isAuthenticated && userRole === 'driver' && 'serviceWorker' in navigator) {
      // Wait a bit for service worker to be ready
      setTimeout(() => {
        registerPushNotifications();
      }, 3000);
    }
  }, [isAuthenticated, userRole]);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    localStorage.removeItem('driverPhone');
    setIsAuthenticated(false);
    setUserRole(null);
    setUserName(null);
    setUserId(null);
    window.location.href = '/login';
  };

  // Login success callback
  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUserRole(userData.role);
    setUserName(userData.name);
    setUserId(userData.id);
    
    // Store phone number if available
    if (userData.phone) {
      localStorage.setItem('driverPhone', userData.phone);
    }
  };

  if (loading) {
    return <div style={{ color: 'white', backgroundColor: '#111827', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }

  return (
    <BrowserRouter>
      <div className="App">
        {/* Navigation Bar - Only shown when authenticated */}
        {isAuthenticated && (
          <nav style={{
            backgroundColor: '#1f2937',
            padding: '10px 20px',
            borderBottom: '2px solid #eab308',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {userRole === 'admin' && (
                <Link to="/admin" style={{ color: '#eab308', textDecoration: 'none', fontWeight: 'bold' }}>
                  🚛 ADMIN
                </Link>
              )}
              {userRole === 'customer' && (
                <Link to="/customer/dashboard" style={{ color: '#eab308', textDecoration: 'none', fontWeight: 'bold' }}>
                  🚚 MY DASHBOARD
                </Link>
              )}
              {userRole === 'driver' && (
                <Link to="/driver/dashboard" style={{ color: '#eab308', textDecoration: 'none', fontWeight: 'bold' }}>
                  🚛 MY JOBS
                </Link>
              )}
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                👤 {userName} ({userRole})
              </span>
              <button
                onClick={handleLogout}
                style={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                LOGOUT
              </button>
            </div>
          </nav>
        )}

        <main>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment-callback" element={<PaymentCallback />} />

            {/* Protected Routes */}
            <Route
              path="/admin"
              element={
                isAuthenticated && userRole === 'admin' ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/customer/dashboard"
              element={
                isAuthenticated && userRole === 'customer' ? (
                  <CustomerDashboard userId={userId} customerName={userName} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/driver/dashboard"
              element={
                isAuthenticated && userRole === 'driver' ? (
                  <DriverDashboard driverId={userId} driverName={userName} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Optional: Keep demo routes for manual testing */}
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/customer/eso" element={<CustomerDashboard userId="857b44ca-763e-4b11-908e-3bb3fd123e19" customerName="Eso Logistics" />} />
            <Route path="/customer/jane" element={<CustomerDashboard userId="69c885a7-a56b-42b1-ba01-6769bce2c109" customerName="Jane Smith" />} />
            <Route path="/driver/turbo" element={<DriverDashboard driverId="77777777-7777-7777-7777-777777777777" driverName="Turbo Tom" />} />
            <Route path="/driver/john" element={<DriverDashboard driverId="65c037c5-857d-49bf-abcb-440d3ca0e25f" driverName="John Doe" />} />
            <Route path="/driver/mack" element={<DriverDashboard driverId="3331a651-8d68-4fc8-9b96-b788c7ea3257" driverName="Mack Truck" />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

export default App;
